import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bookmark, Search, X, Share2, FileText, Sparkles } from 'lucide-react';
import { AppShell } from '../components/AppShell';
type SavedItem = {
  id: string;
  type: 'keyword' | 'semantic';
  source: string;
  title: string;
  alertOrigin: string;
  dateSaved: string;
  savedBy: {
    name: string;
    initials: string;
    color: string;
  };
  note: string;
};
const initialSaved: SavedItem[] = [{
  id: '1',
  type: 'semantic',
  source: 'ANMAT',
  title: 'Disposición 2341/2026 - Actualización de normativas de trazabilidad',
  alertOrigin: 'Cambios en trazabilidad',
  dateSaved: 'Hoy',
  savedBy: {
    name: 'Dr. Rossi',
    initials: 'DR',
    color: 'bg-brand-100 text-brand-700'
  },
  note: 'Revisar con legal antes del viernes. Impacta directamente en el proceso de importación del nuevo lote.'
}, {
  id: '2',
  type: 'semantic',
  source: 'SENASA',
  title: 'Resolución 876/2026 - Restricciones a principios activos',
  alertOrigin: 'Importación principios activos',
  dateSaved: 'Ayer',
  savedBy: {
    name: 'María Gómez',
    initials: 'MG',
    color: 'bg-emerald-100 text-emerald-700'
  },
  note: 'Impacta línea oncológica - coordinar con Comercio Exterior para ajustar la documentación aduanera.'
}, {
  id: '3',
  type: 'keyword',
  source: 'Boletín Oficial',
  title: 'Boletín Oficial Sección 1 - Pág. 34',
  alertOrigin: '"Vademécum PAMI"',
  dateSaved: '28 Mar 2026',
  savedBy: {
    name: 'Dr. Rossi',
    initials: 'DR',
    color: 'bg-brand-100 text-brand-700'
  },
  note: ''
}, {
  id: '4',
  type: 'semantic',
  source: 'ANMAT',
  title: 'Circular 45/2026 - Habilitaciones de depósitos',
  alertOrigin: 'Cambios en trazabilidad',
  dateSaved: '25 Mar 2026',
  savedBy: {
    name: 'J. Pérez',
    initials: 'JP',
    color: 'bg-amber-100 text-amber-700'
  },
  note: 'Atención: Nuevos requisitos de temperatura. Reenviado a Mantenimiento.'
}, {
  id: '5',
  type: 'keyword',
  source: 'Min. Salud',
  title: 'Licitación Pública 45/2026',
  alertOrigin: '"Licitación pública salud"',
  dateSaved: '20 Mar 2026',
  savedBy: {
    name: 'María Gómez',
    initials: 'MG',
    color: 'bg-emerald-100 text-emerald-700'
  },
  note: ''
}];
export function GuardadosPage() {
  const [items, setItems] = useState<SavedItem[]>(initialSaved);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [tempNote, setTempNote] = useState('');
  const handleRemove = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };
  const startEditingNote = (id: string, currentNote: string) => {
    setEditingNoteId(id);
    setTempNote(currentNote);
  };
  const saveNote = (id: string) => {
    setItems(items.map(item => item.id === id ? {
      ...item,
      note: tempNote
    } : item));
    setEditingNoteId(null);
  };
  const filteredItems = items.filter(item => item.title.toLowerCase().includes(searchQuery.toLowerCase()) || item.note.toLowerCase().includes(searchQuery.toLowerCase()) || item.alertOrigin.toLowerCase().includes(searchQuery.toLowerCase()));
  return <AppShell data-id="element-688">
      <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-id="element-689">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-690">
          <div data-id="element-691">
            <h1 className="text-2xl text-navy-950 mb-1" data-id="element-692">Guardados</h1>
            <p className="text-sm text-navy-600" data-id="element-693">
              Documentos marcados y notas de su equipo.
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative max-w-md" data-id="element-694">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} data-id="element-695" />
          <input type="text" placeholder="Buscar en guardados y notas..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full h-10 pl-10 pr-4 rounded-md border border-slate-200 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-white shadow-sm" data-id="element-696" />
        </div>

        {/* Grid/List */}
        <div className="space-y-4" data-id="element-697">
          <AnimatePresence initial={false} data-id="element-698">
            {filteredItems.map(item => <motion.div key={item.id} initial={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95,
            height: 0,
            marginBottom: 0
          }} transition={{
            duration: 0.2
          }} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col sm:flex-row" data-id="element-699">
                {/* Left side: Document Info */}
                <div className="p-5 flex-1 border-b sm:border-b-0 sm:border-r border-slate-100" data-id="element-700">
                  <div className="flex items-start justify-between gap-4 mb-3" data-id="element-701">
                    <div className="flex items-center gap-2" data-id="element-702">
                      <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[10px] font-medium border border-slate-200" data-id="element-703">
                        {item.source}
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-brand-50 text-brand-600 text-[10px] font-medium border border-brand-100 flex items-center gap-1" data-id="element-704">
                        {item.type === 'semantic' ? <Sparkles size={10} data-id="element-705" /> : <FileText size={10} data-id="element-706" />}
                        {item.alertOrigin}
                      </span>
                    </div>
                    <span className="text-xs text-slate-400 shrink-0" data-id="element-707">
                      Guardado {item.dateSaved}
                    </span>
                  </div>

                  <h3 className="text-base font-medium text-navy-950 mb-4" data-id="element-708">
                    {item.title}
                  </h3>

                  <div className="flex items-center justify-between mt-auto" data-id="element-709">
                    <div className="flex items-center gap-2" data-id="element-710">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${item.savedBy.color}`} data-id="element-711">
                        {item.savedBy.initials}
                      </div>
                      <span className="text-xs text-slate-500" data-id="element-712">
                        Guardado por{' '}
                        <span className="font-medium text-navy-950" data-id="element-713">
                          {item.savedBy.name}
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-714">
                      <button className="p-1.5 text-slate-400 hover:text-brand-500 hover:bg-brand-50 rounded-md transition-colors" title="Compartir" data-id="element-715">
                        <Share2 size={16} data-id="element-716" />
                      </button>
                      <button onClick={() => handleRemove(item.id)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors" title="Quitar de guardados" data-id="element-717">
                        <Bookmark size={16} className="fill-current" data-id="element-718" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right side: Notes */}
                <div className="p-5 sm:w-80 bg-slate-50/50 flex flex-col" data-id="element-719">
                  <div className="flex items-center justify-between mb-2" data-id="element-720">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-721">
                      Notas del equipo
                    </h4>
                  </div>

                  {editingNoteId === item.id ? <div className="flex-1 flex flex-col gap-2" data-id="element-722">
                      <textarea value={tempNote} onChange={e => setTempNote(e.target.value)} placeholder="Escriba una nota..." className="w-full flex-1 min-h-[80px] p-3 rounded-md border border-brand-300 text-sm outline-none focus:ring-1 focus:ring-brand-500 resize-none bg-white" autoFocus data-id="element-723" />
                      <div className="flex justify-end gap-2" data-id="element-724">
                        <button onClick={() => setEditingNoteId(null)} className="px-3 py-1.5 text-xs font-medium text-navy-600 hover:bg-slate-200 rounded-md transition-colors" data-id="element-725">
                          Cancelar
                        </button>
                        <button onClick={() => saveNote(item.id)} className="px-3 py-1.5 text-xs font-medium text-white bg-brand-500 hover:bg-brand-400 rounded-md transition-colors" data-id="element-726">
                          Guardar
                        </button>
                      </div>
                    </div> : <div onClick={() => startEditingNote(item.id, item.note)} className={`flex-1 p-3 rounded-md text-sm cursor-pointer transition-colors ${item.note ? 'bg-white border border-slate-200 text-navy-800 hover:border-brand-300' : 'bg-transparent border border-dashed border-slate-300 text-slate-400 hover:bg-white hover:border-brand-300 flex items-center justify-center'}`} data-id="element-727">
                      {item.note ? <p className="whitespace-pre-wrap" data-id="element-728">{item.note}</p> : <span className="flex items-center gap-1.5" data-id="element-729">
                          Agregar nota...
                        </span>}
                    </div>}
                </div>
              </motion.div>)}
          </AnimatePresence>

          {filteredItems.length === 0 && <div className="text-center py-12 bg-white rounded-xl border border-slate-200 border-dashed" data-id="element-730">
              <Bookmark size={32} className="mx-auto text-slate-300 mb-3" data-id="element-731" />
              <h3 className="text-base font-medium text-navy-950 mb-1" data-id="element-732">
                No se encontraron guardados
              </h3>
              <p className="text-sm text-navy-600" data-id="element-733">
                Intente con otros términos de búsqueda.
              </p>
            </div>}
        </div>
      </div>
    </AppShell>;
}